"use client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { personalLinks } from "@/lib/constants";
import { Message } from "@ai-sdk/react";
import { Calendar, Download, Mail } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { IconType } from "react-icons";
import { BiSolidContact } from "react-icons/bi";
import { FaGithub, FaLinkedin, FaTwitter } from "react-icons/fa";
import {
  SiCalendly,
  SiFramework,
  SiHackerrank,
  SiNotion,
} from "react-icons/si";
import { FuturisticLoadingSpinner } from "../loading-spinner";
import { Button } from "../ui/button";

const socialData: {
  icon: IconType;
  href: string;
  label: string;
  target?: string;
}[] = [
  {
    icon: FaGithub,
    href: "https://www.github.com/hamzaMissewi",
    label: "GitHub",
  },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/in/hamza-missewi",
    label: "LinkedIn",
  },
  {
    icon: SiHackerrank,
    href: "https://www.hackerrank.com/profile/hamza_missaoui47",
    label: "HackerRank",
  },
  {
    icon: FaTwitter,
    // href: "https://www.x.com/HamzaMissewi",
    href: personalLinks.x,
    label: "Twitter",
  },
  {
    icon: SiNotion,
    href: "https://www.notion.so/hamza-missaoui/Hamza-Missaoui-Projects-25bba7e86dab8067b6d9d183e6cfeb67",
    label: "My Other Projects",
  },
  {
    icon: Mail,
    href: "mailto:hamza.missaoui28@gmail.com",
    label: "Send Email",
  },
  {
    icon: SiFramework,
    href: "projects",
    label: "Projects Page",
    target: "/projects",
  },
  {
    icon: BiSolidContact,
    href: "contacts",
    label: "Contact Page",
    // target: "_self",
    target: "/contact",
  },
  {
    icon: SiCalendly,
    href: "https://calendly.com/hamzashadow47",
    label: "Calendly",
  },
];

export function SocialLinks() {
  return (
    <div className="flex items-center w-full flex-wrap gap-2 m-2">
      {socialData.map((social, index) => (
        // <a href={social.href} className="p-2 rounded-md bg-white/3 hover:bg-white/6">
        //     <social.icon className='h-3 w-3 mr-1' />
        //     <span>{social.label}</span>
        // </a>
        <Button
          key={index}
          variant="outline"
          size="sm"
          className="rounded-md p-1"
          onClick={() => window.open(social.href, social?.target || "_blank")}
        >
          <social.icon className="h-3 w-3 mr-1" />
          <span className="text-sm">{social.label}</span>
        </Button>
      ))}

      {/* <Badge variant="outline" className="lg:pt-0 pt-2 animate-fade-in-delay-4">
        <Link href="/projects" className="cosmic-button">
          View My Work
        </Link>
      </Badge> */}
    </div>
  );
}

export function SocialLinksTooltip() {
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex justify-center space-x-3 opacity-0 animate-fade-in-up flex-wrap"
        style={{ animationDelay: "1s" }}
      >
        {/* <Link
          href="/contact"
          className="inline-flex text-center border border-white/7 p-3 rounded-full"
        >
          Get in Touch
        </Link> */}

        {/* <Button
                    onClick={() =>
                        window.open(
                            'https://calendly.com/hamzashadow47',
                            '_blank',
                            'noopener,noreferrer'
                        )
                    }
                    className='border-2 bg-white marker:border-white text-black px-8 py-4 rounded-full font-semibold hover:bg-black hover:text-white hover:-translate-y-1 transition-all duration-300'>
                    Get in Touch
                </Button> */}

        {socialData.map(({ icon: Icon, href, label, target }) => (
          <Link
            key={label}
            href={href}
            className="text-center border border-white/7 p-3 w-12 h-12 rounded-full flex items-center justify-center hover:-translate-y-1 transition-all duration-300"
            aria-label={label}
            target={target || "_blank"}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon className="w-5 h-5 dark:text-white text-black hover:text-primary" />
              </TooltipTrigger>
              <TooltipContent side="top" align="center" title={label}>
                {label}
              </TooltipContent>
            </Tooltip>
          </Link>
        ))}
      </div>
    </TooltipProvider>
  );
}

export function AISocialLinks({
  messages,
  setMessages,
}: {
  messages: Message[];
  //  | ((messages: Message[]) => Message[])
  setMessages: (messages: Message[]) => void;
}) {
  const [loading, setLoading] = useState(false);

  const handleDownloadResume = async () => {
    // const resumePath = "/resume/hamza-missaoui-resume-en.pdf";
    const resumePath = "/resume/Hamza_Missaoui_Resume.pdf";
    const defaultFilename = "Hamza_Missaoui_Resume.pdf";

    setLoading(true);
    try {
      // Prompt user for filename
      const userFilename = window.prompt(
        "Enter a filename to save as:",
        defaultFilename
      );

      if (!userFilename) {
        // User clicked cancel
        return;
      }

      setLoading(true);

      // Ensure the filename ends with .pdf
      const filename = userFilename.endsWith(".pdf")
        ? userFilename
        : `${userFilename}.pdf`;

      // Fetch the file first
      const response = await fetch(resumePath);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading resume:", error);
      alert("Failed to download resume. Please try again.");
    } finally {
      // document.body.removeChild(link);
      setLoading(false);
    }
  };

  // const handleDownloadResume = () => {
  //   const resumePath = "/resume/hamza-missaoui-resume-en.pdf";
  //   setLoading(true);
  //   try {
  //     const link = document.createElement("a");
  //     link.href = resumePath;
  //     link.setAttribute("download", resumePath);
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleAction = (
    action:
      | "download-resume"
      | "schedule-meeting"
      | "send-email"
      | "github"
      | "linkedin"
      | "hackerrank"
  ) => {
    switch (action) {
      case "download-resume":
        // Add resume download logic with AI agent tool
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: "Want to view or download Hamza's Resume ?",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            // content: "I'll help you download my resume.",
            content:
              "You can download my resume in 3 languages: [English version](/resume/hamza-missaoui-resume-en.pdf), [French version](/resume/hamza-missaoui-resume-fr.pdf), and [Arabic version](/resume/hamza-missaoui-resume-ar.pdf).",
            // experimental_attachments: [
            //   {
            //     url: "/resume/hamza-missaoui-resume-en.pdf",
            //     name: "Hamza Missaoui Resume (English)",
            //     contentType: "application/pdf",
            //   },
            // ],
          },
        ]);
        handleDownloadResume(); // TODO
        break;
      case "schedule-meeting":
        // Add calendar scheduling logic
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: "I'd like to schedule a meeting with you.",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            content:
              "You can fix a schedule for meeting with me about work through my calendar in [Calendly website](https://calendly.com/hamzashadow47) or [Cal website](https://cal.com/hamza-missewi).",
          },
        ]);
        break;
      case "send-email":
        // Add email sending logic
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: "I want to send an email and get in touch.",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            content:
              "You can connect with me via email [here](mailto:hamza.missaoui28@gmail.com) or by the contact form here.",
          },
        ]);
        break;
      case "github":
        // Add GitHub link
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            // content: "What's your GitHub Profile ?",
            content: "Want to check my GitHub Profile ?",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            content:
              "You can check out my GitHub Profile [here](https://github.com/hamzaMissewi).",
          },
        ]);
        break;
      case "linkedin":
        // Add LinkedIn link
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: "Want to check my LinkedIn Profile?",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            content:
              "You can find my LinkedIn Profile [here](https://linkedin.com/in/hamza--missaoui).",
          },
        ]);
        break;
      case "hackerrank":
        // Add Hackerrank link
        setMessages([
          ...messages,
          {
            id: `user-${Date.now()}`,
            role: "user",
            content: "Want to check my Hackerrank Profile ?",
          },
          {
            id: `assistant-${Date.now() + 1}`,
            role: "assistant",
            content:
              "You can find my HackerRank Profile [here](https://www.hackerrank.com/hamza_missaoui47).",
          },
        ]);
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mb-2">
      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("download-resume")}
      >
        <Download className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        Resume
      </Button>
      {loading && <FuturisticLoadingSpinner />}

      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("schedule-meeting")}
      >
        <Calendar className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        Schedule
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("send-email")}
      >
        <Mail className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        Email
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("github")}
      >
        <FaGithub className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        GitHub
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("linkedin")}
      >
        <FaLinkedin className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        LinkedIn
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={() => handleAction("hackerrank")}
      >
        <FaLinkedin className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
        HackerRank
      </Button>
    </div>
  );
}
